import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, User, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { Logo } from '@/components/ui/logo';

const navigation = [
  { name: 'nav.home', href: '' },
  { name: 'nav.blog', href: '/blog' },
  { name: 'nav.formations', href: '/formations' },
  { name: 'nav.prestations', href: '/prestations' },
  { name: 'nav.contact', href: '/contact' },
];

export function Header() {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  const { locale } = useParams<{ locale: string }>();
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentLocale = locale || i18n.language || 'fr';

  const isActive = (path: string) => {
    const fullPath = `/${currentLocale}${path}`;
    if (path === '') return location.pathname === `/${currentLocale}` || location.pathname === `/${currentLocale}/`;
    return location.pathname.startsWith(fullPath);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <nav className="container mx-auto flex items-center justify-between p-4 lg:px-8">
        {/* Logo */}
        <Link to={`/${currentLocale}`} className="flex items-center">
          <Logo className="h-8 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex md:gap-x-8">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={`/${currentLocale}${item.href}`}
              className={`text-sm font-medium transition-colors hover:text-aspexit-green ${
                isActive(item.href)
                  ? 'text-aspexit-green'
                  : 'text-aspexit-gray'
              }`}
            >
              {t(item.name)}
            </Link>
          ))}
        </div>

        {/* Right side actions */}
        <div className="hidden md:flex md:items-center md:gap-4">
          <LanguageSwitcher />
          {isAdmin && (
            <Button asChild variant="outline" size="sm" className="border-aspexit-green text-aspexit-green hover:bg-aspexit-green/10">
              <Link to={`/${currentLocale}/admin/analytics`} className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                {t('admin.dashboard')}
              </Link>
            </Button>
          )}
          {user ? (
            <Button asChild variant="outline" size="sm">
              <Link to={`/${currentLocale}/profile`} className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('auth.myAccount')}
              </Link>
            </Button>
          ) : (
            <Button asChild variant="default" size="sm" className="bg-aspexit-green hover:bg-aspexit-green-dark">
              <Link to={`/${currentLocale}/auth/login`}>{t('auth.login')}</Link>
            </Button>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="flex md:hidden items-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-aspexit-gray hover:bg-aspexit-gray-light hover:text-aspexit-black"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="sr-only">Open main menu</span>
            {mobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-white">
          <div className="space-y-1 px-4 pb-3 pt-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={`/${currentLocale}${item.href}`}
                className={`block rounded-md px-3 py-2 text-base font-medium ${
                  isActive(item.href)
                    ? 'bg-aspexit-gray-light text-aspexit-green'
                    : 'text-aspexit-gray hover:bg-aspexit-gray-light hover:text-aspexit-black'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t(item.name)}
              </Link>
            ))}
            <div className="pt-4 border-t border-border space-y-1">
              {isAdmin && (
                <Link
                  to={`/${currentLocale}/admin/analytics`}
                  className="block rounded-md px-3 py-2 text-base font-medium text-aspexit-green hover:bg-aspexit-green/10 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Shield className="w-4 h-4" />
                  {t('admin.dashboard')}
                </Link>
              )}
              {user ? (
                <Link
                  to={`/${currentLocale}/profile`}
                  className="block rounded-md px-3 py-2 text-base font-medium text-aspexit-gray hover:bg-aspexit-gray-light hover:text-aspexit-black flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="w-4 h-4" />
                  {t('auth.myAccount')}
                </Link>
              ) : (
                <Link
                  to={`/${currentLocale}/auth/login`}
                  className="block rounded-md px-3 py-2 text-base font-medium bg-aspexit-green text-white hover:bg-aspexit-green-dark"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('auth.login')}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
